import {match} from 'ts-pattern';

import type {TransformedDefinitionApiResponse} from '../types/Api';
import type {CellRendererType, EagColumn} from '../types/EagColumn';

type ColumnResponseBody = TransformedDefinitionApiResponse['columns'][number];

const mapColumnTypeToCellRendererType = (column: ColumnResponseBody): CellRendererType => {
  const renderer = column.renderer as {type: string; decimalPlaces?: number};

  return match<string, CellRendererType>(column.type)
    .with('TEXT', () => {
      if (renderer.type === 'METATEXT') {
        return 'META_TEXT';
      }
      if (renderer.type === 'ICON') {
        return 'ICON';
      }
      return 'PLAIN';
    })
    .with('CODE_LIST', () => 'PLAIN')
    .with('ENUM', () => {
      if (renderer.type === 'FLAG') {
        return 'FLAG';
      }
      if (renderer.type === 'ICON') {
        return 'ICON';
      }
      return 'PLAIN';
    })
    .with('MONEY', () => {
      if (renderer.type === 'MONEY_WITHOUT_DECIMAL') {
        return 'MONEY_NO_DECIMAL';
      }
      if (renderer.type === 'DECIMAL' && renderer.decimalPlaces === 0) {
        return 'MONEY_NO_DECIMAL';
      }
      return 'MONEY';
    })
    .with('FLOAT', () =>
      renderer.type === 'INTEGER' || renderer.type === 'PLAIN' || renderer.type === 'DECIMAL'
        ? 'INTEGER'
        : 'MONEY'
    )
    .with('DATE', () => 'DATE')
    .with('DATE_TIME', () => (renderer.type === 'DATE_TIME' ? 'DATE_TIME' : 'DATE'))
    .with('ENTITY', () => (renderer.type === 'LINK' ? 'LINK' : 'PLAIN'))
    .with('TAG_VALUE', () => 'TAG')
    .with('FILE', () => (renderer.type === 'PLAIN' ? 'FILE_PLAIN' : 'FILE'))
    .with('BOOLEAN', () => (renderer.type === 'CHECKBOX' ? 'CHECKBOX' : 'PLAIN'))
    .with('IMAGE', () => 'THUMBNAIL')
    .with('INTEGER', () =>
      match<string, CellRendererType>(renderer.type)
        .with('PLAIN', () => 'PLAIN')
        .with('FILE_SIZE', () => 'FILE_SIZE')
        .with('ICON', () => 'ICON')
        .otherwise(() => 'INTEGER')
    )
    .with('TIME_INTERVAL', () => 'TIME_INTERVAL')
    .otherwise(() => 'PLAIN');
};

const getRendererParams = (column: ColumnResponseBody): Record<string, unknown> => {
  const renderer = column.renderer as {type: string; decimalPlaces?: number};
  return (
    match<string, Record<string, unknown>>(column.type)
      .with('MONEY', () => ({decimalPlaces: renderer.decimalPlaces ?? 2}))
      // FLOAT columns render a plain number via IntegerCellRenderer: a DECIMAL renderer carries
      // its own decimalPlaces, everything else (INTEGER/PLAIN) renders with no decimals.
      .with('FLOAT', () => ({decimalPlaces: renderer.decimalPlaces ?? 0}))
      .otherwise(() => ({}))
  );
};

export const columnToTypeDefs = (column: ColumnResponseBody): Partial<EagColumn> => ({
  type: mapColumnTypeToCellRendererType(column),
  rendererParams: getRendererParams(column),
});
